import type { DataModelView } from '#core/lib/cognite/view.ts';
import { columnsFromView } from '#core/lib/transforms/column-schema.ts';

import { ProgressDescribeViewsTool, RenderDescribeViewsTool } from './DescribeViewsTool.render.tsx';
import { buildDescribeViewsInputSchema, type DescribeViewsInput, type DescribeViewsOutput } from './schema.ts';
import { QueryKnowledgeGraphTool } from '#plugin-chat/components/tools/query-knowledge-graph/QueryKnowledgeGraphTool.tsx';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { createTool } from '#plugin-chat/lib/tool/create-tool.tsx';
import { ViewNotFoundError } from '#plugin-chat/lib/tool/errors/index.ts';

/**
 * LLM tool that returns the schema (description + typed columns) for one or
 * more CDF Data Model views.
 *
 * Complements {@link QueryKnowledgeGraphTool} by giving the agent a cheap way
 * to discover which properties a view exposes and their types *before*
 * constructing a query — avoiding filter/projection trial-and-error against
 * the knowledge graph.
 */
export const DescribeViewsTool = createTool<
  // { views: [DataModelView, ...DataModelView[]] },
  { views: DataModelView[] },
  DescribeViewsInput,
  DescribeViewsOutput
>('explainViews', ({ views }) => {
  const viewIds = views.map((v) => v.externalId);
  const parametersSchema = buildDescribeViewsInputSchema(viewIds as [string, ...string[]]);
  const viewsById = new Map(views.map((v) => [v.externalId, v]));

  return {
    instructions: (
      <ToolGuidance
        description="Returns view description and a markdown table of columns with type and per-column description for each view."
        tool={DescribeViewsTool}
        whenToUse={[
          `Before calling ${QueryKnowledgeGraphTool.mention} to discover which properties a view exposes and their types`,
          'When you need to pick the right property names for filters or projections',
        ]}
      />
    ),
    config: {
      parameters: parametersSchema,
      render: RenderDescribeViewsTool,
      progress: ProgressDescribeViewsTool,
      description: <p>Describe the schema of one or more CDF Data Model views.</p>,
      execute: async (args: DescribeViewsInput): Promise<DescribeViewsOutput> => {
        const requested = Array.from(new Set(args.views));

        const sections = requested.map((externalId) => {
          const view = viewsById.get(externalId);
          if (!view) {
            throw new ViewNotFoundError(externalId, viewIds);
          }

          const viewDescription =
            (view.schema.out as { description?: string }).description ?? `Data model view: ${externalId}`;

          const columns = columnsFromView(view);
          const rows = columns.map((c) => {
            const type = c.type === 'ref' ? `ref<${c.refKind ?? 'unknown'}>` : c.type;
            const description = (c.description ?? '').replace(/\n/g, ' ').replace(/\|/g, '\\|');
            return `| ${c.name} | ${type} | ${description} |`;
          });

          const table = ['| Column | Type | Description |', '|---|---|---|', ...rows].join('\n');

          return `## ${externalId}\n\n${viewDescription}\n\n${table}`;
        });

        return {
          success: true as const,
          message: sections.join('\n\n'),
        };
      },
    },
  };
});
